import React, { useState, useEffect, useContext } from 'react';
import { bool, shape, string } from 'prop-types';
import { Row } from '@thd-olt-component-react/grid';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { Heading } from '@thd-olt-component-react/heading';
import { Card } from '@thd-olt-component-react/card';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { useProduct } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { BulletList } from './components/BulletList/BulletList';
import { DesktopContent } from './components/DesktopContent/DesktopContent';
import { DownloadAdobe } from './components/DownloadAdobe/DownloadAdobe';
import { MobileContent } from './components/MobileContent/MobileContent';
import { PreviewContent } from './components/PreviewContent/PreviewContent';
import { Prop65 } from './components/Prop65/Prop65';
import { QuickLinks } from './components/QuickLinks/QuickLinks';
import { RichContent } from './components/RichContent/RichContent';
import { KeyFeatures } from './components/KeyFeatures/KeyFeatures';
import './product-overview.scss';

const DOMAINS = ['specificationGroup', 'info', 'media', 'identifiers', 'details'];
let setRichContentExpandableTimer = null;

export const entry = shape({
  name: string,
  value: string,
  bulletedAttr: bool,
  guid: string
});

export const sortByKeyValue = (key) => {
  return (first, second) => (first[key] > second[key]) - (first[key] < second[key]);
};

export const richContentBulletFilter = (item = []) => {
  const regex = /(http(s?):)([/|.|\w|\s|-])*\.(?:jpg|gif|png)/gi;
  return String(typeof item === 'string' ? item : item.value).match(regex);
};

export const loadImage = (url) => {
  return new Promise((resolve, reject) => {
    let image = new Image();
    image.src = url;
    image.onload = () => resolve(image);
    image.onerror = () => reject(image);
  });
};

export const isRichContentExpandable = (richContentsOnlyRef) => {
  const richContentMaxHeight = 500;
  return (richContentsOnlyRef
    && richContentsOnlyRef.current
    && richContentsOnlyRef.current.offsetHeight > richContentMaxHeight);
};

const removeRichContentExpandableTimer = () => {
  clearInterval(setRichContentExpandableTimer);
};

export const isEventBusCreated = () => {
  return typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS;
};

/** THD ProductOverview component. */
export const ProductOverview = ({
  headingTitle,
  itemId,
  showRichContentsOnly,
  quickLinks,
  hideRichContents,
  preview
}) => {
  const { data, loading, error } = useProduct({ itemId, domains: DOMAINS });
  const experienceContext = useContext(ExperienceContext);
  const { channel } = experienceContext;

  const theme = useTheme(ProductOverview, {
    itemId, showRichContentsOnly, quickLinks, hideRichContents, preview
  });
  const { showUpcCode } = theme?.props || {};

  const richContentsOnlyRef = React.createRef();
  const [shouldShowMore, setShouldShowMore] = useState(false);

  const richContentsOnlyOnImageComplete = () => {
    removeRichContentExpandableTimer();
    if (isRichContentExpandable(isRichContentExpandable)) {
      setShouldShowMore(true);
    }
  };

  const setRichContentExpandable = () => {
    if (richContentsOnlyRef
      && richContentsOnlyRef.current
      && richContentsOnlyRef.current.querySelector) {
      // interval is added only because etch has been added after the DOM creation
      setRichContentExpandableTimer = setInterval(() => {
        const image = richContentsOnlyRef.current.querySelector('img');
        const imageSrc = image ? image.src : null;
        if (imageSrc) {
          loadImage(imageSrc)
            .then(richContentsOnlyOnImageComplete, removeRichContentExpandableTimer);
        }
      }, 1000);
    }
  };

  const triggerCardClick = (cardOnClick) => {
    if ((isEventBusCreated())) {
      window.LIFE_CYCLE_EVENT_BUS.on('product-overview.card-click', ({ output }) => {
        const { scrollToBottom = false } = output;
        cardOnClick(this, 'default-overlay', scrollToBottom);
      });
    }
  };

  useEffect(() => {
    setRichContentExpandable();
    if (isEventBusCreated()) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-overview.ready', {});
    }
    return () => {
      removeRichContentExpandableTimer();
    };
  }, [itemId]);

  if (loading || (error && !data) || !data?.product) {
    return null;
  }

  const {
    modelNumber,
    storeSkuNumber,
    upc
  } = data?.product?.identifiers || {};

  const {
    richContent
  } = data?.product?.media || {};

  const {
    prop65Warning
  } = data?.product?.info || {};

  const {
    description,
    highlights,
    infoAndGuides: documents,
    descriptiveAttributes
  } = data?.product?.details || {};

  let bullets = (descriptiveAttributes || []).filter((attr) => attr.bulleted);
  if (showRichContentsOnly) {
    bullets = bullets
      .filter(richContentBulletFilter);
  }
  if (prop65Warning) {
    bullets = [{
      name: 'AAA_prop65',
      value: <Prop65 channel={channel} value="Warning: Cancer and Reproductive Harm - www.p65warnings.ca.gov" />,
      bulleted: true
    }, ...bullets];
  }

  bullets.sort(sortByKeyValue('name'));

  // @TODO dont map hightlight to value:
  const highlightedBullets = (highlights || []).map((highlight) => ({ value: highlight }));
  const renderedHighlights = (highlights || []).length > 0
    && (
      <BulletList
        bullets={highlightedBullets}
        showRichContentsOnly={showRichContentsOnly}
        hideRichContents={hideRichContents}
      />
    );

  return (
    showRichContentsOnly
      ? (
        <RichContent
          description={description}
          documents={documents}
          richContent={richContent}
          bullets={bullets}
          showRichContentsOnly={showRichContentsOnly}
          hideRichContents={hideRichContents}
          preview={preview}
          shouldShowMore={shouldShowMore}
          richContentsOnlyRef={richContentsOnlyRef}
          setShouldShowMore={setShouldShowMore}
          channel={channel}
        />
      )
      : (
        <div
          className="grid pip-row section product-overview"
          data-section="product-overview"
        >
          <Card getOnClick={triggerCardClick}>
            <Heading title={headingTitle} underline />
            <DesktopContent
              description={description}
              documents={documents}
              richContent={richContent}
              bullets={bullets}
              hideRichContents={hideRichContents}
              preview={preview}
              showRichContentsOnly={showRichContentsOnly}
              shouldShowMore={shouldShowMore}
              channel={channel}
            />
            <Card.Content title="Product Overview" fade>
              <PreviewContent highlights={renderedHighlights || description} />
              <Card.Content.Overlay>
                <MobileContent
                  description={description}
                  documents={documents}
                  highlights={renderedHighlights}
                  itemId={itemId}
                  modelNumber={modelNumber}
                  storeSkuNumber={storeSkuNumber}
                  bullets={bullets}
                  showRichContentsOnly={showRichContentsOnly}
                  hideRichContents={hideRichContents}
                  upc={upc}
                  showUpcCode={showUpcCode}
                  channel={channel}
                />
              </Card.Content.Overlay>
            </Card.Content>
            <Card.Footer className="product-overview__model-information" hideTopBorder>
              <ProductInfoBar itemId={itemId} showOnMobile />
            </Card.Footer>
            { quickLinks && (
              <Card.Footer className="product-overview__quick-links" hideTopBorder>
                <Row className="product-overview__quick-links-block flush">
                  {' '}
                  <QuickLinks itemId={itemId} />
                  {' '}
                </Row>
                <DownloadAdobe />
              </Card.Footer>
            )}
            {!quickLinks && !!documents?.length
              && (
                <Card.Footer
                  className="product-overview__mobile-footer"
                  triggerFor="default-overlay"
                  scrollToBottom
                  hideTopBorder
                >
                  <span className="product-overview__mobile-footer-link bttn--link">Info &amp; Guides</span>
                </Card.Footer>
              )}
          </Card>
        </div>
      )
  );
};

ProductOverview.themeProps = {
  showUpcCode: bool
};

ProductOverview.defaultThemeProps = {
  showUpcCode: false
};

ProductOverview.displayName = 'ProductOverview';

ProductOverview.propTypes = {
  headingTitle: string,
  hideRichContents: bool,
  preview: bool,
  quickLinks: bool,
  showRichContentsOnly: bool,
  itemId: string.isRequired,
};

ProductOverview.defaultProps = {
  headingTitle: 'Product Overview',
  hideRichContents: false,
  preview: false,
  quickLinks: false,
  showRichContentsOnly: false,
};

export { QuickLinks, KeyFeatures };
